import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, normalizePaginatedList } from "../lib/api";
import { buildListParams, DEFAULT_LIST_PAGE_SIZE, EMPTY_LIST_META } from "../lib/listPagination";
import { useDataTable } from "./useDataTable";
import { LIST_FILTER_DEBOUNCE_MS, useDebouncedValue } from "./useDebouncedValue";

const EMPTY_EXTRA_PARAMS = {};

/**
 * Server-paginated list via a page bootstrap endpoint (exact fields only).
 * Extra top-level keys (e.g. pending_approvals, aggregates) are returned in `extras`.
 */
export function useBootstrapList({
  path,
  columns,
  defaultSort = null,
  extraParams = EMPTY_EXTRA_PARAMS,
  enabled = true,
  wsTypes = [],
  filterDebounceMs = LIST_FILTER_DEBOUNCE_MS,
}) {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(EMPTY_LIST_META);
  const [extras, setExtras] = useState({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_LIST_PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const table = useDataTable(rows, columns, defaultSort, { serverSide: true });
  const appliedFilters = useDebouncedValue(table.columnFilters, filterDebounceMs);

  const extraKey = JSON.stringify(extraParams ?? EMPTY_EXTRA_PARAMS);
  const filterKey = JSON.stringify(appliedFilters);
  const sortKey = JSON.stringify(table.sorting ?? null);
  const listQueryKey = `${filterKey}|${sortKey}|${extraKey}|${pageSize}`;
  const prevListQueryKey = useRef(listQueryKey);

  const reload = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    if (!enabled) return undefined;
    if (prevListQueryKey.current !== listQueryKey) {
      prevListQueryKey.current = listQueryKey;
      if (page !== 1) {
        setPage(1);
        return undefined;
      }
    }
    let cancelled = false;
    setLoading(true);
    const params = buildListParams(
      page,
      pageSize,
      appliedFilters,
      table.sorting,
      columns,
      extraParams,
    );
    api
      .get(path, { params })
      .then((r) => {
        if (cancelled) return;
        const data = r.data || {};
        const body = normalizePaginatedList(data);
        setRows(body.results);
        setMeta({
          count: body.count,
          page: body.page,
          page_size: body.page_size,
          total_pages: body.total_pages,
          summary: body.summary,
        });
        const {
          count: _c,
          page: _p,
          page_size: _ps,
          total_pages: _tp,
          results: _r,
          summary: _s,
          exported: _e,
          ...rest
        } = data;
        setExtras(rest);
      })
      .catch(() => {
        if (!cancelled) {
          setRows([]);
          setMeta(EMPTY_LIST_META);
          setExtras({});
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // listQueryKey encodes filters/sort/extras — do not add object refs (extraParams, table) as deps
  }, [path, page, pageSize, listQueryKey, refreshKey, enabled, appliedFilters]);

  const exportParams = useMemo(
    () => buildListParams(1, pageSize, appliedFilters, table.sorting, columns, extraParams),
    [filterKey, sortKey, extraKey, pageSize, columns, appliedFilters],
  );

  const pagination = useMemo(
    () => ({
      meta,
      pageSize,
      onPageSizeChange: setPageSize,
      onPageChange: setPage,
    }),
    [meta, pageSize],
  );

  return {
    table,
    rows,
    meta,
    extras,
    loading,
    page,
    pageSize,
    setPage,
    setPageSize,
    pagination,
    reload,
    exportParams,
    wsTypes,
  };
}

/**
 * Single-shot page bootstrap (dashboards, lab tab, accounts dual-list).
 */
export function usePageBootstrap({ path, params, enabled = true, wsTypes = [] }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const paramKey = useMemo(() => JSON.stringify(params || {}), [params]);

  const reload = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    setLoading(true);
    api
      .get(path, { params })
      .then((r) => {
        if (!cancelled) setData(r.data || null);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [path, paramKey, refreshKey, enabled]);

  return { data, loading, reload, wsTypes };
}

/**
 * Paginated list nested inside a bootstrap payload (e.g. stack-sample-intake → samples).
 */
export function useNestedBootstrapList({
  path,
  listKey,
  columns,
  defaultSort = null,
  extraParams = EMPTY_EXTRA_PARAMS,
  enabled = true,
  wsTypes = [],
}) {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(EMPTY_LIST_META);
  const [extras, setExtras] = useState({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_LIST_PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const table = useDataTable(rows, columns, defaultSort, { serverSide: true });
  const appliedFilters = useDebouncedValue(table.columnFilters, LIST_FILTER_DEBOUNCE_MS);
  const extraKey = JSON.stringify(extraParams ?? EMPTY_EXTRA_PARAMS);
  const filterKey = JSON.stringify(appliedFilters);
  const sortKey = JSON.stringify(table.sorting ?? null);
  const listQueryKey = `${filterKey}|${sortKey}|${extraKey}|${pageSize}`;
  const prevListQueryKey = useRef(listQueryKey);

  const reload = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    if (!enabled) return undefined;
    if (prevListQueryKey.current !== listQueryKey) {
      prevListQueryKey.current = listQueryKey;
      if (page !== 1) {
        setPage(1);
        return undefined;
      }
    }
    let cancelled = false;
    setLoading(true);
    const params = buildListParams(page, pageSize, appliedFilters, table.sorting, columns, extraParams);
    api
      .get(path, { params })
      .then((r) => {
        if (cancelled) return;
        const data = r.data || {};
        const listBody = normalizePaginatedList(data[listKey] || {});
        setRows(listBody.results);
        setMeta({
          count: listBody.count,
          page: listBody.page,
          page_size: listBody.page_size,
          total_pages: listBody.total_pages,
          summary: listBody.summary,
        });
        const { [listKey]: _list, ...rest } = data;
        setExtras(rest);
      })
      .catch(() => {
        if (!cancelled) {
          setRows([]);
          setMeta(EMPTY_LIST_META);
          setExtras({});
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [path, listKey, page, pageSize, listQueryKey, refreshKey, enabled, appliedFilters]);

  const exportParams = useMemo(
    () => buildListParams(1, pageSize, appliedFilters, table.sorting, columns, extraParams),
    [filterKey, sortKey, extraKey, pageSize, columns, appliedFilters],
  );

  const pagination = useMemo(
    () => ({
      meta,
      pageSize,
      onPageSizeChange: setPageSize,
      onPageChange: setPage,
    }),
    [meta, pageSize],
  );

  return {
    table,
    rows,
    meta,
    extras,
    loading,
    page,
    pageSize,
    pagination,
    reload,
    exportParams,
    wsTypes,
  };
}

function prefixListParams(params, prefix, pageKey, orderingKey) {
  const out = {};
  for (const [key, value] of Object.entries(params)) {
    if (key === "page") {
      out[pageKey] = value;
    } else if (key === "page_size") {
      out.page_size = value;
    } else if (key === "ordering") {
      out[orderingKey] = value;
    } else {
      out[`${prefix}_${key}`] = value;
    }
  }
  return out;
}

/** Accounts page — pending purchases + vouchers in one bootstrap call. */
export function useAccountsBootstrap({
  pendingColumns,
  voucherColumns,
  pendingDefaultSort = null,
  voucherDefaultSort = null,
  pendingSearch = "",
  voucherSearch = "",
  enabled = true,
  include = "both",
}) {
  const [pendingRows, setPendingRows] = useState([]);
  const [pendingMeta, setPendingMeta] = useState(EMPTY_LIST_META);
  const [voucherRows, setVoucherRows] = useState([]);
  const [voucherMeta, setVoucherMeta] = useState(EMPTY_LIST_META);
  const [aggregates, setAggregates] = useState({});
  const [pendingPage, setPendingPage] = useState(1);
  const [voucherPage, setVoucherPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_LIST_PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const debouncedPendingSearch = useDebouncedValue(pendingSearch.trim(), LIST_FILTER_DEBOUNCE_MS);
  const debouncedVoucherSearch = useDebouncedValue(voucherSearch.trim(), LIST_FILTER_DEBOUNCE_MS);

  const pendingTable = useDataTable(pendingRows, pendingColumns, pendingDefaultSort, { serverSide: true });
  const voucherTable = useDataTable(voucherRows, voucherColumns, voucherDefaultSort, { serverSide: true });
  const appliedPendingFilters = useDebouncedValue(pendingTable.columnFilters, LIST_FILTER_DEBOUNCE_MS);
  const appliedVoucherFilters = useDebouncedValue(voucherTable.columnFilters, LIST_FILTER_DEBOUNCE_MS);

  const pendingFilterKey = useMemo(() => JSON.stringify(appliedPendingFilters), [appliedPendingFilters]);
  const voucherFilterKey = useMemo(() => JSON.stringify(appliedVoucherFilters), [appliedVoucherFilters]);
  const pendingSortKey = useMemo(() => JSON.stringify(pendingTable.sorting), [pendingTable.sorting]);
  const voucherSortKey = useMemo(() => JSON.stringify(voucherTable.sorting), [voucherTable.sorting]);

  useEffect(() => {
    setPendingPage(1);
  }, [pendingFilterKey, pendingSortKey, pageSize, debouncedPendingSearch]);

  useEffect(() => {
    setVoucherPage(1);
  }, [voucherFilterKey, voucherSortKey, pageSize, debouncedVoucherSearch]);

  const reload = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    setLoading(true);
    const pendingParams = buildListParams(
      pendingPage,
      pageSize,
      appliedPendingFilters,
      pendingTable.sorting,
      pendingColumns,
    );
    const voucherParams = buildListParams(
      voucherPage,
      pageSize,
      appliedVoucherFilters,
      voucherTable.sorting,
      voucherColumns,
    );
    const params = {
      include,
      ...prefixListParams(pendingParams, "pending", "pending_page", "pending_ordering"),
      voucher_page: voucherPage,
      page_size: pageSize,
      voucher_ordering: voucherParams.ordering,
      voucher_code: voucherParams.code,
      voucher_purchase_code: voucherParams.purchase_code,
      voucher_party_name: voucherParams.party_name,
      voucher_variety: voucherParams.variety,
      voucher_type: voucherParams.voucher_type,
    };
    if (debouncedPendingSearch) params.pending_search = debouncedPendingSearch;
    if (debouncedVoucherSearch) params.voucher_search = debouncedVoucherSearch;
    api
      .get("/accounts/bootstrap", { params })
      .then((r) => {
        if (cancelled) return;
        const data = r.data || {};
        const pending = normalizePaginatedList(data.pending || {});
        const vouchers = normalizePaginatedList(data.vouchers || {});
        setPendingRows(pending.results);
        setPendingMeta({
          count: pending.count,
          page: pending.page,
          page_size: pending.page_size,
          total_pages: pending.total_pages,
        });
        setVoucherRows(vouchers.results);
        setVoucherMeta({
          count: vouchers.count,
          page: vouchers.page,
          page_size: vouchers.page_size,
          total_pages: vouchers.total_pages,
        });
        setAggregates(data.aggregates || {});
      })
      .catch(() => {
        if (!cancelled) {
          setPendingRows([]);
          setVoucherRows([]);
          setPendingMeta(EMPTY_LIST_META);
          setVoucherMeta(EMPTY_LIST_META);
          setAggregates({});
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    enabled,
    include,
    pendingPage,
    voucherPage,
    pageSize,
    pendingFilterKey,
    voucherFilterKey,
    pendingSortKey,
    voucherSortKey,
    refreshKey,
    pendingColumns,
    voucherColumns,
    debouncedPendingSearch,
    debouncedVoucherSearch,
  ]);

  const pendingExportParams = useMemo(
    () => buildListParams(1, pageSize, appliedPendingFilters, pendingTable.sorting, pendingColumns, { state: "ACCOUNTS_PENDING" }),
    [pendingFilterKey, pendingSortKey, pageSize, pendingColumns, appliedPendingFilters],
  );
  const voucherExportParams = useMemo(
    () => buildListParams(1, pageSize, appliedVoucherFilters, voucherTable.sorting, voucherColumns),
    [voucherFilterKey, voucherSortKey, pageSize, voucherColumns, appliedVoucherFilters],
  );

  return {
    pending: {
      table: pendingTable,
      rows: pendingRows,
      meta: pendingMeta,
      loading,
      pagination: {
        meta: pendingMeta,
        pageSize,
        onPageSizeChange: setPageSize,
        onPageChange: setPendingPage,
      },
      exportParams: pendingExportParams,
    },
    vouchers: {
      table: voucherTable,
      rows: voucherRows,
      meta: voucherMeta,
      loading,
      pagination: {
        meta: voucherMeta,
        pageSize,
        onPageSizeChange: setPageSize,
        onPageChange: setVoucherPage,
      },
      exportParams: voucherExportParams,
    },
    aggregates,
    loading,
    reload,
    wsTypes: ["purchase"],
  };
}

/** Accounts payments tab — cash vouchers + material targets for issuance. */
export function usePaymentsBootstrap({ cashColumns, defaultSort = null, enabled = true }) {
  const [cashRows, setCashRows] = useState([]);
  const [cashMeta, setCashMeta] = useState(EMPTY_LIST_META);
  const [materialTargets, setMaterialTargets] = useState([]);
  const [aggregates, setAggregates] = useState({});
  const [cashPage, setCashPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_LIST_PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const cashTable = useDataTable(cashRows, cashColumns, defaultSort, { serverSide: true });
  const appliedCashFilters = useDebouncedValue(cashTable.columnFilters, LIST_FILTER_DEBOUNCE_MS);
  const cashFilterKey = useMemo(() => JSON.stringify(appliedCashFilters), [appliedCashFilters]);
  const cashSortKey = useMemo(() => JSON.stringify(cashTable.sorting), [cashTable.sorting]);

  useEffect(() => {
    setCashPage(1);
  }, [cashFilterKey, cashSortKey, pageSize]);

  const reload = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    setLoading(true);
    const cashParams = buildListParams(cashPage, pageSize, appliedCashFilters, cashTable.sorting, cashColumns);
    const params = prefixListParams(cashParams, "cash", "cash_page", "cash_ordering");
    api
      .get("/accounts/payments/bootstrap", { params })
      .then((r) => {
        if (cancelled) return;
        const data = r.data || {};
        const cash = normalizePaginatedList(data.cash_vouchers || {});
        setCashRows(cash.results);
        setCashMeta({
          count: cash.count,
          page: cash.page,
          page_size: cash.page_size,
          total_pages: cash.total_pages,
        });
        setMaterialTargets(data.material_targets || []);
        setAggregates(data.aggregates || {});
      })
      .catch(() => {
        if (!cancelled) {
          setCashRows([]);
          setCashMeta(EMPTY_LIST_META);
          setMaterialTargets([]);
          setAggregates({});
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, cashPage, pageSize, cashFilterKey, cashSortKey, refreshKey, cashColumns]);

  return {
    cash: {
      table: cashTable,
      rows: cashRows,
      meta: cashMeta,
      loading,
      pagination: {
        meta: cashMeta,
        pageSize,
        onPageSizeChange: setPageSize,
        onPageChange: setCashPage,
      },
    },
    materialTargets,
    aggregates,
    loading,
    reload,
    wsTypes: ["voucher"],
  };
}

/** Cash or bank ledger register — payments + receipts. */
export function useCashLedgerBootstrap({
  ledgerColumns,
  defaultSort = null,
  enabled = true,
  paymentMode = "CASH",
}) {
  const [ledgerRows, setLedgerRows] = useState([]);
  const [ledgerMeta, setLedgerMeta] = useState(EMPTY_LIST_META);
  const [materialTargets, setMaterialTargets] = useState([]);
  const [partyPickers, setPartyPickers] = useState([]);
  const [aggregates, setAggregates] = useState({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_LIST_PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const ledgerTable = useDataTable(ledgerRows, ledgerColumns, defaultSort, { serverSide: true });
  const appliedLedgerFilters = useDebouncedValue(ledgerTable.columnFilters, LIST_FILTER_DEBOUNCE_MS);
  const filterKey = useMemo(() => JSON.stringify(appliedLedgerFilters), [appliedLedgerFilters]);
  const sortKey = useMemo(() => JSON.stringify(ledgerTable.sorting), [ledgerTable.sorting]);
  const mode = String(paymentMode || "CASH").toUpperCase() === "BANK" ? "BANK" : "CASH";

  useEffect(() => {
    setPage(1);
  }, [filterKey, sortKey, pageSize, mode]);

  const reload = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    setLoading(true);
    const listParams = buildListParams(page, pageSize, appliedLedgerFilters, ledgerTable.sorting, ledgerColumns);
    const params = {
      page: listParams.page,
      page_size: listParams.page_size,
      ordering: listParams.ordering,
      code: listParams.code,
      party_name: listParams.party_name,
      entry_type: listParams.entry_type,
      payment_mode: mode,
    };
    api
      .get("/cash-ledger/bootstrap", { params })
      .then((r) => {
        if (cancelled) return;
        const data = r.data || {};
        const ledger = normalizePaginatedList(data.ledger || {});
        setLedgerRows(ledger.results);
        setLedgerMeta({
          count: ledger.count,
          page: ledger.page,
          page_size: ledger.page_size,
          total_pages: ledger.total_pages,
        });
        setMaterialTargets(data.material_targets || []);
        setPartyPickers(data.party_pickers || []);
        setAggregates(data.aggregates || {});
      })
      .catch(() => {
        if (!cancelled) {
          setLedgerRows([]);
          setLedgerMeta(EMPTY_LIST_META);
          setMaterialTargets([]);
          setPartyPickers([]);
          setAggregates({});
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, page, pageSize, filterKey, sortKey, refreshKey, ledgerColumns, mode]);

  return {
    ledger: {
      table: ledgerTable,
      rows: ledgerRows,
      meta: ledgerMeta,
      loading,
      pagination: {
        meta: ledgerMeta,
        pageSize,
        onPageSizeChange: setPageSize,
        onPageChange: setPage,
      },
    },
    materialTargets,
    partyPickers,
    aggregates,
    loading,
    reload,
    wsTypes: ["voucher"],
  };
}

/** Users admin page — users + roles + permissions in one bootstrap call. */
export function useUsersBootstrap({
  userColumns,
  roleColumns,
  userDefaultSort = null,
  roleDefaultSort = null,
  enabled = true,
}) {
  const [userRows, setUserRows] = useState([]);
  const [userMeta, setUserMeta] = useState(EMPTY_LIST_META);
  const [roleRows, setRoleRows] = useState([]);
  const [roleMeta, setRoleMeta] = useState(EMPTY_LIST_META);
  const [permissions, setPermissions] = useState([]);
  const [userPage, setUserPage] = useState(1);
  const [rolePage, setRolePage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_LIST_PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const userTable = useDataTable(userRows, userColumns, userDefaultSort, { serverSide: true });
  const roleTable = useDataTable(roleRows, roleColumns, roleDefaultSort, { serverSide: true });
  const appliedUserFilters = useDebouncedValue(userTable.columnFilters, LIST_FILTER_DEBOUNCE_MS);
  const appliedRoleFilters = useDebouncedValue(roleTable.columnFilters, LIST_FILTER_DEBOUNCE_MS);

  const userFilterKey = useMemo(() => JSON.stringify(appliedUserFilters), [appliedUserFilters]);
  const roleFilterKey = useMemo(() => JSON.stringify(appliedRoleFilters), [appliedRoleFilters]);
  const userSortKey = useMemo(() => JSON.stringify(userTable.sorting), [userTable.sorting]);
  const roleSortKey = useMemo(() => JSON.stringify(roleTable.sorting), [roleTable.sorting]);

  useEffect(() => {
    setUserPage(1);
  }, [userFilterKey, userSortKey, pageSize]);

  useEffect(() => {
    setRolePage(1);
  }, [roleFilterKey, roleSortKey, pageSize]);

  const reload = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    setLoading(true);
    const userParams = buildListParams(userPage, pageSize, appliedUserFilters, userTable.sorting, userColumns);
    const roleParams = buildListParams(rolePage, pageSize, appliedRoleFilters, roleTable.sorting, roleColumns);
    const params = {
      user_page: userPage,
      role_page: rolePage,
      page_size: pageSize,
      user_ordering: userParams.ordering,
      role_ordering: roleParams.ordering,
      name: userParams.name,
      email: userParams.email,
      role: userParams.role,
      role_name: roleParams.name,
      slug: roleParams.slug,
      description: roleParams.description,
    };
    api
      .get("/users/bootstrap", { params })
      .then((r) => {
        if (cancelled) return;
        const data = r.data || {};
        const users = normalizePaginatedList(data.users || {});
        const roles = normalizePaginatedList(data.roles || {});
        setUserRows(users.results);
        setUserMeta({
          count: users.count,
          page: users.page,
          page_size: users.page_size,
          total_pages: users.total_pages,
        });
        setRoleRows(roles.results);
        setRoleMeta({
          count: roles.count,
          page: roles.page,
          page_size: roles.page_size,
          total_pages: roles.total_pages,
        });
        setPermissions(data.permissions || []);
      })
      .catch(() => {
        if (!cancelled) {
          setUserRows([]);
          setRoleRows([]);
          setUserMeta(EMPTY_LIST_META);
          setRoleMeta(EMPTY_LIST_META);
          setPermissions([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    enabled,
    userPage,
    rolePage,
    pageSize,
    userFilterKey,
    roleFilterKey,
    userSortKey,
    roleSortKey,
    refreshKey,
    userColumns,
    roleColumns,
  ]);

  return {
    userList: {
      table: userTable,
      rows: userRows,
      meta: userMeta,
      loading,
      pagination: {
        meta: userMeta,
        pageSize,
        onPageSizeChange: setPageSize,
        onPageChange: setUserPage,
      },
      exportParams: buildListParams(1, pageSize, appliedUserFilters, userTable.sorting, userColumns),
      reload,
    },
    roleList: {
      table: roleTable,
      rows: roleRows,
      meta: roleMeta,
      loading,
      pagination: {
        meta: roleMeta,
        pageSize,
        onPageSizeChange: setPageSize,
        onPageChange: setRolePage,
      },
      exportParams: buildListParams(1, pageSize, appliedRoleFilters, roleTable.sorting, roleColumns),
      reload,
    },
    permissions,
    loading,
    reload,
    wsTypes: [],
  };
}
